<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="lg"
  >
    <template #header>
      <h4>{{ isEditMode ? $t('components.wirepasLocations.modal.editTitle') : $t('components.wirepasLocations.modal.createTitle') }}</h4>
    </template>

    <template #body>
      <div>
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- Podstawowe informacje -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-info-circle me-2" />
              {{ $t('components.assetGroups.modal.basicInfo') }}
            </h5>

            <div class="mb-3">
              <label
                for="wl-name"
                class="form-label required"
              >
                {{ $t('components.assetGroups.modal.fields.name') }}
              </label>
              <input
                id="wl-name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': nameError }"
                :placeholder="$t('components.assetGroups.modal.placeholders.name')"
              >
              <div
                v-if="nameError"
                class="invalid-feedback"
              >
                {{ nameError }}
              </div>
            </div>
          </div>

          <!-- Typ lokalizacji -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-tag me-2" />
              {{ $t('components.wirepasLocations.columns.type') }}
            </h5>
            <div class="d-flex flex-wrap gap-2">
              <button
                v-for="(label, key) in locationTypeOptions"
                :key="key"
                type="button"
                class="wirepas-type-btn"
                :class="{ 'wirepas-type-btn--active': form.locationType === key, [`wirepas-type-btn--${key}`]: true }"
                @click="form.locationType = form.locationType === key ? null : key"
              >
                {{ label }}
              </button>
            </div>
          </div>

          <!-- Adres i współrzędne -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-geo-alt me-2" />
              {{ $t('components.assetGroups.modal.location') }}
            </h5>

            <div class="row">
              <div class="col-md-12 mb-3">
                <label
                  for="wl-street"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.street') }}
                </label>
                <input
                  id="wl-street"
                  v-model="form.street"
                  type="text"
                  class="form-control"
                  :placeholder="$t('components.assetGroups.modal.placeholders.street')"
                >
              </div>
            </div>

            <div class="row">
              <div class="col-md-4 mb-3">
                <label
                  for="wl-city"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.city') }}
                </label>
                <input
                  id="wl-city"
                  v-model="form.city"
                  type="text"
                  class="form-control"
                  :placeholder="$t('components.assetGroups.modal.placeholders.city')"
                >
              </div>
              <div class="col-md-4 mb-3">
                <label
                  for="wl-postal"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.postalCode') }}
                </label>
                <input
                  id="wl-postal"
                  v-model="form.postalCode"
                  type="text"
                  class="form-control"
                  :placeholder="$t('components.assetGroups.modal.placeholders.postalCode')"
                >
              </div>
              <div class="col-md-4 mb-3">
                <label
                  for="wl-country"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.country') }}
                </label>
                <select
                  id="wl-country"
                  v-model="form.country"
                  class="form-select"
                >
                  <option :value="null">
                    {{ $t('components.assetGroups.modal.selectCountry') }}
                  </option>
                  <option
                    v-for="(countryName, countryKey) in countries"
                    :key="countryKey"
                    :value="countryKey"
                  >
                    {{ countryName }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-12">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="loadingGeocode || !form.street || !form.city"
                  @click="handleGeocode"
                >
                  <span
                    v-if="loadingGeocode"
                    class="spinner-border spinner-border-sm me-2"
                  />
                  <i
                    v-else
                    class="bi bi-geo-alt-fill me-2"
                  />
                  {{ $t('components.assetGroups.modal.geocodeButton') }}
                </button>
                <div
                  v-if="geocodeError"
                  class="alert alert-danger alert-sm mt-2 mb-0"
                >
                  <i class="bi bi-exclamation-triangle-fill me-2" />{{ geocodeError }}
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label
                  for="wl-lat"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.latitude') }}
                </label>
                <input
                  id="wl-lat"
                  v-model.number="form.lat"
                  type="number"
                  step="0.000001"
                  class="form-control"
                  min="-90"
                  max="90"
                >
              </div>
              <div class="col-md-6 mb-3">
                <label
                  for="wl-lng"
                  class="form-label"
                >
                  {{ $t('components.assetGroups.modal.fields.longitude') }}
                </label>
                <input
                  id="wl-lng"
                  v-model.number="form.lng"
                  type="number"
                  step="0.000001"
                  class="form-control"
                  min="-180"
                  max="180"
                >
              </div>
            </div>
          </div>

          <!-- Bramy Wirepas -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-broadcast-pin me-2" />
              {{ $t('components.wirepasLocations.detail.gateways') }}
            </h5>

            <div
              v-if="gatewaysLoading"
              class="d-flex align-items-center gap-2 text-muted"
            >
              <span class="spinner-border spinner-border-sm" />
              {{ $t('components.wirepasLocations.loading') }}
            </div>
            <div v-else>
              <div class="asset-select-wrapper position-relative">
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="bi bi-broadcast-pin" />
                  </span>
                  <input
                    ref="gwSearchInputRef"
                    v-model="gwSearch"
                    type="text"
                    class="form-control"
                    :placeholder="$t('components.wirepasLocations.modal.gatewaySearch')"
                    autocomplete="off"
                    @focus="showGwDropdown = true"
                    @blur="onGwBlur"
                  >
                  <button
                    v-if="gwSearch"
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="gwSearch = ''"
                  >
                    <i class="bi bi-x" />
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @mousedown.prevent="showGwDropdown = !showGwDropdown"
                  >
                    <i
                      class="bi"
                      :class="showGwDropdown ? 'bi-chevron-up' : 'bi-chevron-down'"
                    />
                  </button>
                </div>

                <div
                  v-if="showGwDropdown"
                  class="asset-dropdown-results"
                >
                  <div
                    v-if="!filteredGateways.length"
                    class="dropdown-item text-muted"
                  >
                    <i class="bi bi-info-circle me-2" />
                    {{ gwSearch ? $t('components.wirepasLocations.modal.noGatewaysFound') : $t('components.wirepasLocations.modal.noGateways') }}
                  </div>
                  <div
                    v-for="gw in filteredGateways"
                    :key="gw.gwId"
                    class="dropdown-item asset-dropdown-item"
                    @mousedown.prevent="addGateway(gw)"
                  >
                    <span
                      class="wirepas-status-dot me-2"
                      :class="gw.state === 'online' ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                    />
                    <i class="bi bi-plus-circle me-2 text-success" />
                    <span class="asset-name">{{ gw.gwId }}</span>
                    <small
                      v-if="gw.gatewayModel"
                      class="text-muted ms-2"
                    >({{ gw.gatewayModel }})</small>
                    <span
                      v-if="gw.assignedLocationId && gw.assignedLocationId !== currentLocationId"
                      class="badge bg-warning text-dark ms-2"
                    >
                      {{ $t('components.wirepasLocations.modal.assignedElsewhere') }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="selectedGateways.length"
                class="selected-assets-list mt-3"
              >
                <label class="form-label text-muted small mb-2">
                  {{ $t('components.wirepasLocations.detail.gateways') }} ({{ selectedGateways.length }})
                </label>
                <div class="selected-assets-grid">
                  <div
                    v-for="gw in selectedGateways"
                    :key="gw.gwId"
                    class="selected-asset-badge"
                  >
                    <span
                      class="wirepas-status-dot"
                      :class="gw.state === 'online' ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                    />
                    <span>{{ gw.gwId }}</span>
                    <button
                      type="button"
                      class="btn-remove"
                      @click="removeGateway(gw.gwId)"
                    >
                      <i class="bi bi-x" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else-if="!gatewaysLoading"
                class="text-muted small mt-2"
              >
                {{ $t('components.wirepasLocations.modal.noGatewaysSelected') }}
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
        >
          {{ $t('components.assetGroupsWithLocation.modal.close') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="handleSubmit"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
          />
          {{ isEditMode ? $t('components.assetGroups.modal.update') : $t('components.assetGroups.modal.create') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/components/content/_modals/core/BaseModal.vue'
import { createAssetGroup, updateAssetGroup } from '@/crud/asset-group'
import { getGeocode } from '@/crud/getters'
import { getWirepasGateways, updateWirepasLocationGateways } from '@/crud/wirepas-location'

type GwItem = {
  gwId: string
  state: string
  gatewayModel: string | null
  assignedLocationId: number | null
}

export default defineComponent({
  name: 'WirepasLocationFormModal',
  components: { BaseModal },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t, tm } = useI18n()
    const modalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const isEditMode = ref(false)
    const currentLocationId = ref<number | null>(null)
    const currentAssetGroupId = ref<number | null>(null)
    const loading = ref(false)
    const loadingGeocode = ref(false)
    const gatewaysLoading = ref(false)
    const errorMessage = ref<string | null>(null)
    const geocodeError = ref<string | null>(null)
    const nameError = ref<string | null>(null)

    const locationTypeOptions = computed<Record<string, string>>(() => ({
      hala: t('components.wirepasLocations.locationTypes.hala'),
      crossdock: t('components.wirepasLocations.locationTypes.crossdock'),
      sklep: t('components.wirepasLocations.locationTypes.sklep'),
    }))

    const form = ref({
      name: '',
      locationType: null as string | null,
      street: null as string | null,
      city: null as string | null,
      postalCode: null as string | null,
      country: 'poland' as string | null,
      lat: null as number | null,
      lng: null as number | null,
    })

    const allGateways = ref<GwItem[]>([])
    const selectedGateways = ref<GwItem[]>([])
    const gwSearch = ref('')
    const showGwDropdown = ref(false)
    const gwSearchInputRef = ref<HTMLInputElement | null>(null)

    const filteredGateways = computed(() =>
      allGateways.value.filter(
        (g) =>
          !selectedGateways.value.some((s) => s.gwId === g.gwId) &&
          (!gwSearch.value || g.gwId.toLowerCase().includes(gwSearch.value.toLowerCase()) || (g.gatewayModel ?? '').toLowerCase().includes(gwSearch.value.toLowerCase()))
      )
    )

    const selectedGwIds = computed(() => selectedGateways.value.map((g) => g.gwId))

    const countries = computed<Record<string, string>>(() => {
      const msgs = tm('components.history.summary.countries')
      return msgs && typeof msgs === 'object' && !Array.isArray(msgs) ? msgs as Record<string, string> : {}
    })

    const resetForm = () => {
      isEditMode.value = false
      currentLocationId.value = null
      currentAssetGroupId.value = null
      form.value = { name: '', locationType: null, street: null, city: null, postalCode: null, country: 'poland', lat: null, lng: null }
      selectedGateways.value = []
      allGateways.value = []
      gwSearch.value = ''
      showGwDropdown.value = false
      errorMessage.value = null
      geocodeError.value = null
      nameError.value = null
    }

    const addGateway = (gw: GwItem) => {
      if (!selectedGateways.value.some((s) => s.gwId === gw.gwId)) {
        selectedGateways.value.push(gw)
      }
      gwSearch.value = ''
    }

    const removeGateway = (gwId: string) => {
      selectedGateways.value = selectedGateways.value.filter((g) => g.gwId !== gwId)
    }

    const onGwBlur = () => {
      setTimeout(() => { showGwDropdown.value = false }, 180)
    }

    const loadGateways = async (locationId: number | null) => {
      gatewaysLoading.value = true
      try {
        const res = await getWirepasGateways()
        allGateways.value = res.data?.data ?? []
        if (locationId) {
          selectedGateways.value = allGateways.value.filter(g => g.assignedLocationId === locationId)
        }
      } catch {
        allGateways.value = []
      } finally {
        gatewaysLoading.value = false
      }
    }

    const openModal = async (locationData?: any) => {
      resetForm()
      if (locationData) {
        isEditMode.value = true
        currentLocationId.value = locationData.id ?? null
        currentAssetGroupId.value = locationData.assetGroupId ?? null
        form.value.name = locationData.name ?? ''
        form.value.locationType = locationData.type ?? null
        form.value.street = locationData.address?.street ?? null
        form.value.city = locationData.address?.city ?? null
        form.value.postalCode = locationData.address?.postalCode ?? null
        form.value.country = locationData.address?.country ?? 'poland'
        form.value.lat = locationData.lat ? parseFloat(locationData.lat) : null
        form.value.lng = locationData.lng ? parseFloat(locationData.lng) : null
      }
      modalRef.value?.openModal()
      await loadGateways(currentLocationId.value)
    }

    const closeModal = () => {
      modalRef.value?.closeModal()
      resetForm()
    }

    const handleGeocode = async () => {
      geocodeError.value = null
      const parts: string[] = []
      if (form.value.street) parts.push(form.value.street)
      if (form.value.city) parts.push(form.value.city)
      if (form.value.postalCode) parts.push(form.value.postalCode)
      if (form.value.country) {
        const name = countries.value[form.value.country]
        if (name) parts.push(name)
      }
      if (!parts.length) return
      loadingGeocode.value = true
      try {
        const res = await getGeocode(parts.join(', '))
        if (res.data?.lat && res.data?.lng) {
          form.value.lat = parseFloat(res.data.lat)
          form.value.lng = parseFloat(res.data.lng)
        } else {
          geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
        }
      } catch {
        geocodeError.value = t('components.assetGroups.modal.errors.geocodeFailed')
      } finally {
        loadingGeocode.value = false
      }
    }

    const handleSubmit = async () => {
      nameError.value = null
      errorMessage.value = null

      if (!form.value.name.trim() || form.value.name.trim().length < 3) {
        nameError.value = t('components.assetGroups.modal.validation.nameMinLength', { min: 3 })
        return
      }

      loading.value = true
      try {
        const payload = {
          name: form.value.name.trim(),
          location: {
            street: form.value.street,
            city: form.value.city,
            postalCode: form.value.postalCode,
            country: form.value.country,
            lat: form.value.lat,
            lng: form.value.lng,
          }
        }

        let locationId = currentLocationId.value

        if (isEditMode.value && currentAssetGroupId.value) {
          await updateAssetGroup(currentAssetGroupId.value, payload)
        } else {
          const res = await createAssetGroup({ ...payload, isActive: true })
          locationId = res.data?.group?.location?.id ?? res.data?.location?.id ?? null
        }

        if (locationId) {
          await updateWirepasLocationGateways(locationId, selectedGwIds.value as string[], form.value.locationType)
        }

        emit('saved', isEditMode.value)
        closeModal()
      } catch (e: any) {
        errorMessage.value = e?.response?.data?.error || e?.message || t('components.assetGroupsWithLocation.modal.error')
      } finally {
        loading.value = false
      }
    }

    return {
      modalRef,
      isEditMode,
      currentLocationId,
      locationTypeOptions,
      loading,
      loadingGeocode,
      gatewaysLoading,
      errorMessage,
      geocodeError,
      nameError,
      form,
      allGateways,
      filteredGateways,
      selectedGateways,
      gwSearch,
      showGwDropdown,
      gwSearchInputRef,
      countries,
      openModal,
      closeModal,
      addGateway,
      removeGateway,
      onGwBlur,
      handleGeocode,
      handleSubmit,
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/modal/asset-group-form-modal.scss';
@import '@/assets/scss/wirepas-locations-view.scss';

// Bootstrap's .form-control/.form-select only get a dark-mode override inside the unrelated
// .m-geofencing scope in dark-theme-style.scss — this modal has none, so its inputs stayed
// white-on-dark. Scope the fix here instead of growing that global allowlist further.
.form-control,
.form-select {
  background-color: var(--card-bg, #fff);
  color: var(--text-color, #252422);
  border-color: var(--border-color, rgba(0, 0, 0, 0.15));

  &::placeholder { color: var(--text-muted, #888); }

  &:focus {
    background-color: var(--card-bg, #fff);
    color: var(--text-color, #252422);
  }

  &:disabled { opacity: 0.6; }
}

.input-group-text {
  background-color: var(--card-bg, #f5f8fa);
  color: var(--text-muted, #888);
  border-color: var(--border-color, rgba(0, 0, 0, 0.15));
}

// asset-group-form-modal.scss (shared with other modals) hardcodes light-mode-only colors for
// these — overridden here rather than there so other modals using that file are untouched.
.form-section {
  border-bottom-color: var(--border-color, #e5e7eb);
}

.form-section-title {
  color: var(--text-color, #111827);
}

.asset-dropdown-results {
  background: var(--card-bg, #fff);
  border-color: var(--border-color, #dee2e6);
}

.asset-dropdown-item {
  color: var(--text-color, #111827);
  border-bottom-color: var(--border-color, #f3f4f6);

  &:hover {
    background: var(--card-bg-hover, #f0fdf4);
  }
}

// dark-theme-style.scss sets a blanket `span { color: var(--text-color) }` in dark mode, which
// beats this pill's own (more specific in class count, but fewer type selectors) text color —
// the gateway name rendered muted grey on the badge's light-green fill, unreadable. This badge
// is an intentionally theme-independent colored tag (like the type/status badges elsewhere),
// so re-assert its own color rather than switching it to a CSS var.
.selected-asset-badge,
.selected-asset-badge span {
  color: #166534 !important;
}

// Metronic's base .btn-outline-secondary fills with a fixed light grey even at rest (not just
// on hover) — on a dark card this reads as a plain white button with an invisible icon.
.input-group .btn-outline-secondary {
  background-color: transparent !important;
  color: var(--text-muted, #6c757d) !important;
  border-color: var(--border-color, #ced4da) !important;

  &:hover,
  &:focus,
  &:active {
    background-color: var(--card-bg-hover, rgba(0, 0, 0, 0.06)) !important;
    color: var(--text-color, #252422) !important;
    border-color: var(--border-color, #ced4da) !important;
  }
}

.wirepas-type-btn {
  padding: 6px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid transparent;
  background: rgba(0, 0, 0, 0.06);
  color: #555;
  transition: all 0.12s;

  &:hover {
    background: rgba(var(--primary-color-rgb), 0.1);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }

  &--hala.wirepas-type-btn--active {
    background: rgba(72, 112, 184, 0.14);
    border-color: #4870b8;
    color: #4870b8;
  }

  &--crossdock.wirepas-type-btn--active {
    background: rgba(169, 128, 30, 0.14);
    border-color: #a9801e;
    color: #a9801e;
  }

  &--sklep.wirepas-type-btn--active {
    background: rgba(63, 143, 82, 0.14);
    border-color: #3f8f52;
    color: #3f8f52;
  }
}
</style>
